import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { PreviewTransaction, CurrencyType } from '../types';

interface TransactionPreviewProps {
  transactions: PreviewTransaction[];
  targetCurrency: CurrencyType;
  onToggleTransaction: (id: string) => void;
  onSelectAll: (selected: boolean) => void;
}

interface TransactionPreviewItemProps {
  transaction: PreviewTransaction;
  targetCurrency: CurrencyType;
  onToggle: (id: string) => void;
  noPayeeLabel: string;
}

const TransactionPreviewItem = React.memo(function TransactionPreviewItem({
  transaction,
  targetCurrency,
  onToggle,
  noPayeeLabel,
}: TransactionPreviewItemProps) {
  const numericAmount = parseFloat(transaction.amount);
  const isNegative = !Number.isNaN(numericAmount) && numericAmount < 0;

  const formattedAmount = Number.isNaN(numericAmount)
    ? transaction.amount
    : numericAmount.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

  const amountDisplay = `${formattedAmount} ${targetCurrency}`;

  return (
    <label
      className={`transaction-preview-item ${transaction.selected ? 'selected' : 'deselected'}`}
    >
      <input
        type="checkbox"
        checked={transaction.selected}
        onChange={() => onToggle(transaction.id)}
      />
      <div className="transaction-preview-details">
        <div className="transaction-preview-row">
          <span className="transaction-preview-date">{transaction.date}</span>
          <span className={`transaction-preview-amount ${isNegative ? 'negative' : 'positive'}`}>
            {amountDisplay}
          </span>
        </div>
        <div className="transaction-preview-payee">
          {transaction.payee || noPayeeLabel}
        </div>
        {transaction.description && (
          <div className="transaction-preview-description">{transaction.description}</div>
        )}
        {transaction.categoryName && (
          <div className="transaction-preview-category">{transaction.categoryName}</div>
        )}
      </div>
    </label>
  );
});

const TransactionPreview: React.FC<TransactionPreviewProps> = ({
  transactions,
  targetCurrency,
  onToggleTransaction,
  onSelectAll,
}) => {
  const { t } = useTranslation();
  const selectAllRef = useRef<HTMLInputElement>(null);
  const selectedCount = transactions.filter((transaction) => transaction.selected).length;
  const allSelected = transactions.length > 0 && selectedCount === transactions.length;
  const noneSelected = selectedCount === 0;
  const noPayeeLabel = t('noPayee', { ns: 'converter' });

  useEffect(() => {
    const checkbox = selectAllRef.current;
    if (checkbox) {
      checkbox.indeterminate = !allSelected && !noneSelected;
    }
  }, [allSelected, noneSelected]);

  const handleSelectAllChange = () => {
    onSelectAll(!allSelected);
  };

  return (
    <div className="transaction-preview">
      <div className="transaction-preview-header">
        <h4>{t('previewTitle', { ns: 'converter' })}</h4>
        <p className="transaction-preview-subtitle">
          {t('previewSubtitle', {
            ns: 'converter',
            selected: selectedCount,
            total: transactions.length,
          })}
        </p>
      </div>

      <div className="transaction-preview-controls">
        <div className="transaction-select-all">
          <input
            id="transaction-select-all"
            ref={selectAllRef}
            type="checkbox"
            checked={allSelected}
            onChange={handleSelectAllChange}
          />
          <label htmlFor="transaction-select-all">{t('selectAll', { ns: 'converter' })}</label>
        </div>
      </div>

      <div className="transaction-preview-list">
        {transactions.map((transaction) => (
          <TransactionPreviewItem
            key={transaction.id}
            transaction={transaction}
            targetCurrency={targetCurrency}
            onToggle={onToggleTransaction}
            noPayeeLabel={noPayeeLabel}
          />
        ))}
      </div>

      {noneSelected && (
        <p className="transaction-preview-warning">
          {t('noTransactionsSelected', { ns: 'converter' })}
        </p>
      )}
    </div>
  );
};

export default TransactionPreview;
